/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { AzureCsAccountKind } from 'src/core/stores/AzureCsAccountStore';
import { AuthoringRegion } from 'src/core/types/regionTypes';
import { tryParseJson } from 'src/core/utils/base';
import { getStorage } from 'src/core/utils/getStorage';
import { LuisConstants } from 'src/core/utils/LuisConstants';

export type ApiVersion = 'v2.0' | 'v3.0-preview' | 'v3.1' | 'v3.2-preview.2';
export type ApiName =
    | 'luis'
    | 'customText'
    | 'clu'
    | 'textAnalytics'
    | 'qna'
    | 'cluRuntime'
    | 'translation'
    | 'qnaRuntime'
    | 'generateQnaRuntime'
    | 'languageTextAnalytics'
    | 'abstractiveSummarization'
    | 'conversationSummarization';
export enum UnifiedAuthoringDevEnvironments {
    UnifiedAuthoringPpeApim = 'https://westus2-tip.api.cognitive.microsoft.com',
    UnifiedAuthoringPpe = 'https://language-authoring.usw2.ppe.api.cog.trafficmanager.net/svc/apis',
    UnifiedAuthoringStable = 'https://language-authoring-dev1.usc.dev.api.cog.trafficmanager.net/svc/apis',
    UnifiedAuthoringNightly = 'https://language-authoring-dev1.usc.dev.api.cog.trafficmanager.net/svc/apis',
    UnifiedAuthoringWithLoveEast = 'https://language-authoring-dev2.usc.dev.api.cog.trafficmanager.net/svc/apis',
    UnifiedAuthoringWithLoveCentral = 'https://language-authoring-dev2.usc.dev.api.cog.trafficmanager.net/svc/apis'
}
export enum LuisDevEnvironments {
    Dialogice3 = 'https://luis-authoring-dice3.usc.dev.api.cog.trafficmanager.net/svc/authoring',
    Dialogice4 = 'https://luis-authoring-dice4.usc.dev.api.cog.trafficmanager.net/svc/authoring',
    Dialogice6 = 'https://luis-authoring-dice6.usc.dev.api.cog.trafficmanager.net/svc/authoring',
    Dialogice14 = 'https://luis-authoring-dice14.usc.dev.api.cog.trafficmanager.net/svc/authoring'
}

type Environment = {
    /**
     * The current deployment region.
     */
    defaultRegion: AuthoringRegion;

    /**
     * Whether this deployment is on a production
     * environment or not.
     */
    isProduction: boolean;

    /**
     * The URL used for accessing public programmatic
     * apis through CIG.
     */
    apiUrl: string;

    /**
     * The URL used for accessing private programmatic
     * apis through CIG.
     */
    webApiUrl: string;

    /**
     * The URL used for accessing private authentication
     * web apis directly through the LUIS backend.
     */
    authWebApiUrl: string;

    /**
     * The blob store url corresponding to this region.
     */
    assetsUrl: string;

    /**
     * The url to use to fetch Azure resources.
     */
    azureBaseUrl: string;

    /**
     * The suffix to reach storage blobs. Ex. blob.core.windows.net, blob.core.usgovcloudapi.net, etc.
     */
    storageBlobSuffix: string;
};

/**
 * Provides options for getting an environment.
 */
type GetEnvironmentOptions = {
    /**
     * The name of the API. Defaults to luis.
     */
    apiName?: ApiName;

    /**
     * The version of the API. Defaults to v2.0
     */
    apiVersion?: ApiVersion;

    /**
     * The selected authoring resource's endpoint.
     */
    currentResourceEndpoint?: string;

    /**
     * The selected authoring resource's kind.
     */
    currentResourceKind?: AzureCsAccountKind;
};

const { getData } = getStorage('localStorage');
const luisDevBaseUrl = getData(LuisConstants.StorageKeys.LuisDevBaseUrlStorageKey) ?? LuisDevEnvironments.Dialogice4;
const unifiedAuthoringDevBaseUrl =
    getData(LuisConstants.StorageKeys.UnifiedAuthoringBaseUrlStorageKey) ?? UnifiedAuthoringDevEnvironments.UnifiedAuthoringStable;

// This method was used when the API version was in the path to generate the correct path accordingly
const generateLuisApiPathFromVersion = (apiVersion: ApiVersion) => {
    switch (apiVersion) {
        case 'v3.0-preview':
            return {
                devApiPathName: 'api/v3.0-preview',
                devWebApiPathName: 'api/v2.0',
                productionApiPathName: 'luis/authoring/v3.0-preview',
                productionWebApiPathName: 'luis/webApi/v2.0'
            };
        case 'v2.0':
        default:
            return {
                devApiPathName: 'api/v2.0',
                devWebApiPathName: 'api/v2.0',
                productionApiPathName: 'luis/api/v2.0',
                productionWebApiPathName: 'luis/webApi/v2.0'
            };
    }
};

export const isDevelopmentEnvironment = () => !window.luisAppSettings.isProduction;
export const isProductionEnvironment = () => {
    const isProductionDevEnvEnabled = tryParseJson<boolean>(getData(LuisConstants.StorageKeys.ProductionDevEnvironmentEnabled)) ?? false;

    return !isDevelopmentEnvironment() || isProductionDevEnvEnabled;
};
export const isStagingEnvironment = () => {
    const { hostname } = window.location;

    const stagingHostNames = [
        LuisConstants.Luis.StagingHostName,
        LuisConstants.LanguageStudio.StagingHostName,
        LuisConstants.LanguageStudio.LegacyStagingHostName
    ];

    return stagingHostNames.some(h => h === hostname);
};

export const isProductionUrl = () => {
    const { hostname } = window.location;

    const productonHostNames = [
        LuisConstants.Luis.HostName,
        LuisConstants.Luis.EXHostName,
        LuisConstants.Luis.CanaryHostName,
        LuisConstants.Luis.FairFaxHostName,
        LuisConstants.Luis.MoonCakeHostName,
        LuisConstants.LanguageStudio.HostName,
        LuisConstants.Luis.WestEuropeHostName,
        LuisConstants.LanguageStudio.EXHostName,
        LuisConstants.LanguageStudio.RXHostName,
        LuisConstants.LanguageStudio.LegacyHostName,
        LuisConstants.LanguageStudio.FairFaxHostName,
        LuisConstants.LanguageStudio.MoonCakeHostName
    ];

    return productonHostNames.some(h => h === hostname);
};

export const getEnvironment = (options?: GetEnvironmentOptions): Environment => {
    const { azureBaseUrl, defaultRegion, storageBlobSuffix } = window.luisAppSettings;
    const { apiName = 'luis', apiVersion = 'v2.0', currentResourceEndpoint, currentResourceKind } = options ?? {};

    const { hostname } = window.location;
    const isProductionMode = isProductionEnvironment();

    const generateLuisUrls = (apiType: 'api' | 'webApi') => {
        const { devApiPathName, devWebApiPathName, productionApiPathName, productionWebApiPathName } = generateLuisApiPathFromVersion(
            apiVersion
        );

        if (isProductionMode) {
            return `${currentResourceEndpoint}/${apiType === 'api' ? productionApiPathName : productionWebApiPathName}`;
        }
        return `${luisDevBaseUrl}/${apiType === 'api' ? devApiPathName : devWebApiPathName}`;
    };

    const generateEnvironmentUrls = (apiType: 'api' | 'webApi') => {
        switch (currentResourceKind) {
            case 'TextAnalytics':
                let prefix: string;
                let suffix: string;

                switch (apiName) {
                    case 'luis':
                        return generateLuisUrls(apiType);
                    case 'customText':
                        prefix = isProductionMode ? currentResourceEndpoint : `${unifiedAuthoringDevBaseUrl}/`;
                        suffix = apiType === 'webApi' ? 'language/analyze-text' : 'language/authoring/analyze-text';
                        break;
                    case 'clu':
                        prefix = isProductionMode ? currentResourceEndpoint : `${unifiedAuthoringDevBaseUrl}/`;
                        suffix = apiType === 'webApi' ? 'language/analyze-conversations' : 'language/authoring/analyze-conversations';
                        break;
                    case 'qna':
                        prefix = currentResourceEndpoint;
                        suffix = 'language/query-knowledgebases';
                        break;
                    case 'cluRuntime':
                        prefix = currentResourceEndpoint;
                        suffix = 'language';
                        break;
                    case 'qnaRuntime':
                        prefix = currentResourceEndpoint;
                        suffix = 'language';
                        break;
                    case 'languageTextAnalytics':
                        prefix = currentResourceEndpoint;
                        suffix = 'language';
                        break;
                    case 'textAnalytics':
                    default:
                        prefix = currentResourceEndpoint;
                        suffix = `text/analytics/${apiVersion}`;
                        break;
                }

                return `${prefix}${suffix}`;
            case 'TextTranslation':
                return `${currentResourceEndpoint}`;
            case 'LUIS':
            case 'LUIS.Authoring':
            default:
                return generateLuisUrls(apiType);
        }
    };

    return {
        azureBaseUrl,
        isProduction: isProductionMode,
        assetsUrl: `https://${hostname}`,
        authWebApiUrl: `https://${hostname}`,
        apiUrl: generateEnvironmentUrls('api'),
        webApiUrl: generateEnvironmentUrls('webApi'),
        defaultRegion: <AuthoringRegion>defaultRegion,
        storageBlobSuffix
    };
};
